/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	View,
	TextField,
	ActionButton,
	Pressable,
	ErrorText,
	Checkbox,
} from '@components/atoms'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import { User } from '@utils'

interface PropsType {
	label?: string
	errorMessage?: string
	isResetPassword?: boolean
	onChange?: (value: string) => void
	variant?: 'filled' | 'outlined' | 'standard'
}

/** 패스워드 입력 — 랜덤 생성/직접 입력 토글. 입력은 테마의 밑줄(standard) 을 따른다 */
export const ResetPasswordTextField = (props: PropsType) => {
	const { label, errorMessage, isResetPassword, onChange, variant } = props

	const [password, setPassword] = React.useState<string>('')
	const [isRandomPassword, setIsRandomPassword] = React.useState<boolean>(true)
	const [passwordType, setPasswordType] = React.useState<string>('password')

	const isError: boolean = errorMessage !== undefined && errorMessage !== ''

	React.useEffect(() => {
		if (isResetPassword) {
			getRandomPassword()
			setIsRandomPassword(true)
		}
	}, [isResetPassword])

	React.useEffect(() => {
		if (isRandomPassword) {
			getRandomPassword()
		}
	}, [isRandomPassword])

	React.useEffect(() => {
		if (onChange) {
			onChange(password)
		}
	}, [password])

	const getRandomPassword = React.useCallback(() => {
		const newPassword: string = User.getRandomPassword()
		setPassword(newPassword)
	}, [password])

	const onManualPasswordChange = React.useCallback((e: any) => {
		const str = e.target.value
		setPassword(str)
	}, [])

	const onRandonPasswordCheckboxClick = React.useCallback(() => {
		setPassword('')
		setIsRandomPassword(!isRandomPassword)
	}, [isRandomPassword])

	const onShowPassword = () => {
		const newType = passwordType === 'password' ? 'default' : 'password'
		setPasswordType(newType)
	}

	return (
		<View css={style}>
			{isRandomPassword ? (
				<View className="textFieldView">
					<TextField
						className="columnTextField"
						size="small"
						label={label ?? ''}
						value={password}
						error={isError}
						variant={variant ?? 'standard'}
					/>
					<View className="rightAccessoryView">
						<ActionButton
							className="showPasswordButton"
							iconName="reload"
							title="새로 생성"
							variant="text"
							onClick={getRandomPassword}
						/>
					</View>
				</View>
			) : (
				<View className="textFieldView">
					<TextField
						label="패스워드"
						size="small"
						type={passwordType}
						value={password}
						onChange={onManualPasswordChange}
						variant={variant ?? 'standard'}
					/>
					<View className="rightAccessoryView">
						<ActionButton
							className="showPasswordButton"
							title={passwordType === 'password' ? '보기' : '가리기'}
							iconName={passwordType === 'password' ? 'eye-outline' : 'eye-off-outline'}
							variant="text"
							onClick={onShowPassword}
						/>
					</View>
				</View>
			)}

			<View className="randomPasswordYnView">
				<Pressable className="randomToggle" onClick={onRandonPasswordCheckboxClick}>
					<Checkbox checked={isRandomPassword} />
					<span className="randomPasswordYnTitle">랜덤 패스워드</span>
				</Pressable>
			</View>

			{isError && (
				<View>
					<ErrorText className="errorMessage">{errorMessage}</ErrorText>
				</View>
			)}
		</View>
	)
}

const style = css({
	width: '100%',
	fontFamily: boFont.ui,

	'.textFieldView': {
		position: 'relative',
		width: '100%',

		'.columnTextField': {
			width: '100%',
		},

		'.rightAccessoryView': {
			position: 'absolute',
			top: 8,
			right: 0,

			'.showPasswordButton': {
				width: 'fit-content',
				height: 'fit-content',
			},
		},
	},

	'.randomPasswordYnView': {
		marginTop: 8,
		marginLeft: -6,

		'.randomToggle': {
			display: 'flex',
			alignItems: 'center',
			gap: 4,
			padding: 0,
		},
		'.randomPasswordYnTitle': {
			fontSize: 13,
			fontWeight: 300,
			color: mono.dim,
		},
	},

	'.errorMessage': { marginTop: 6 },
})
